import type { Device } from '@studio/models/device';
import type { PTZ } from '@studio/models/ptz';
import { hasMovement } from '@studio/models/ptz';
import { useDocumentActive, useLatestFn, useLazyRef, useMount } from '@studio/react-utils/hooks';
import { noop } from 'lodash-es';
import { useEffect } from 'react';
import { Choreographer } from '../choreographer.js';
import { PtzCommands, QuickCommands } from './constants.js';
import type { Wiring } from './types.js';
import { useAutoTrackingControlProtection } from '#~/auto-tracking/control-protection/hooks.js';
import type { Command } from '#~/command/index.js';
import { move, quickMove } from '#~/control/movement/commands/index.js';
import { usePiston } from '#~/control/movement/hooks.js';
import { useFormula } from '#~/hot-keys/hooks.js';
import { evaluate } from '#~/hot-keys/index.js';

const useChoreographer = (deviceId: Device['id']): Choreographer => {
  const piston = usePiston(
    deviceId,
    useLatestFn((intent: PTZ) => {
      move(deviceId, intent, { toolUsed: 'keyboard' });
    })
  );

  const { isProtected, triggerAlert: triggerControlAlert } =
    useAutoTrackingControlProtection(deviceId) ?? {};

  const handleMove = useLatestFn((intent: PTZ) => {
    if (isProtected && hasMovement(intent)) {
      triggerControlAlert?.();

      return;
    }

    piston.process(intent);
  });

  return useLazyRef(() => new Choreographer(handleMove)).current;
};

/** @internal */
export const useWiring = (deviceId: Device['id']): Wiring => {
  const choreographer = useChoreographer(deviceId);
  const isDocumentActive = useDocumentActive();
  const formula = useFormula();

  // Handle keyup events.
  const handleKeyUp = useLatestFn((event: KeyboardEvent) => {
    // Run the event through the hot key processor.
    const directive = evaluate(formula, event);

    if (!directive) {
      return;
    }

    // Does this command involve the choreographer?
    const movement = PtzCommands[directive.command];

    if (movement) {
      choreographer.remove(movement);
    }
  });

  useMount(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    // Listen for keyup events.
    document.addEventListener('keyup', handleKeyUp, { signal });

    return () => {
      // Cleanup event listeners.
      abortController.abort();

      // Stop all movement on unmount.
      choreographer.stop();
    };
  });

  // Stop all movement when the document loses focus.
  useEffect(() => {
    if (!isDocumentActive) {
      choreographer.stop();
    }
  }, [choreographer, isDocumentActive]);

  // Listen to incoming commands.
  return {
    listen: useLatestFn((command: Command) => {
      // Does this command involve keyboard movement?
      let movement = PtzCommands[command];

      if (movement) {
        choreographer.add(movement);

        return;
      }

      // How about quick movement?
      movement = QuickCommands[command];

      if (movement) {
        quickMove(deviceId, movement, false, { toolUsed: 'quick_move' }).catch(noop);
      }
    })
  };
};
